import { useMemo } from "react";
import { MediaItem } from "shared/types";
import { PhotosForm } from "shared/app/PhotosForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { Unit } from "types/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { PhotoWithStatus } from "shared/app/PhotosForm/types";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

const getSmallMedia = (media: MediaItem) => {
  return media.attachments?.find((a) => a.resolution === "small");
};

const getFullMedia = (media: MediaItem) => {
  return media.attachments?.find((a) => a.resolution === "full");
};

const transformMedia = (media: Unit["media"]): PhotoWithStatus[] => {
  return (
    media?.map((media) => {
      const smallMedia = getSmallMedia(media);
      const fullMedia = getFullMedia(media);
      return {
        id: media.id.toString(),
        status: "uploaded",
        resolutions: {
          small: {
            file: new File([], ""),
            uploadedUrl: smallMedia?.url || ""
          },
          full: {
            file: new File([], ""),
            uploadedUrl: fullMedia?.url || ""
          }
        },
        previewUrl: smallMedia?.url || ""
      };
    }) || []
  );
};

interface PhotosSectionProps {
  propertyId: number;
  singleFamilyId?: number;
  propertyMedia?: MediaItem[];
}

export const PhotosSection = ({
  propertyId,
  singleFamilyId,
  propertyMedia
}: PhotosSectionProps) => {
  const queryClient = useQueryClient();
  const { propertyEditDeniedTooltip, unitEditDeniedTooltip } =
    useEditDetailsPermissions();

  const { data: unitMedia } = useQuery({
    queryKey: ["unit", String(singleFamilyId)],
    queryFn: () => addressApi.getUnitById(String(singleFamilyId)),
    enabled: Boolean(singleFamilyId),
    select: (data) => data.media
  });

  // Self-fetch property media via the dedicated slice. Seeded with the inline
  // value from the parent property GET (slim default) so there's no extra
  // request when it's already there; refetched after upload via mutation.
  const propertyMediaQueryKey = useMemo(
    () => ["properties", String(propertyId), "media"],
    [propertyId]
  );
  const { data: fetchedPropertyMedia } = useQuery<MediaItem[]>({
    queryKey: propertyMediaQueryKey,
    queryFn: () => addressApi.getPropertyMedia(propertyId),
    initialData: propertyMedia,
    initialDataUpdatedAt: propertyMedia ? Date.now() : undefined,
    enabled: !singleFamilyId,
    staleTime: Infinity
  });

  const { mutate: uploadMedia, isPending } = useMutation({
    mutationFn: (media) => {
      if (singleFamilyId)
        return addressApi.editUnit(singleFamilyId, {
          media_session_key: sessionKey,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          media: media as any
        });

      return addressApi.editProperty({
        media_session_key: sessionKey,
        propertyId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        media: media as any
      });
    },
    onError: (error) => {
      toast.error(`Failed to upload photos: ${getErrorMessage(error)}`);
    },
    onSuccess: () => {
      onSuccess();
    }
  });

  const onSuccess = singleFamilyId
    ? () =>
        queryClient.invalidateQueries({
          queryKey: ["unit", String(singleFamilyId)]
        })
    : () => {
        queryClient.invalidateQueries({ queryKey: propertyMediaQueryKey });
        queryClient.invalidateQueries({
          queryKey: ["properties", String(propertyId)]
        });
      };

  return (
    <PhotosForm
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange={(media) => uploadMedia(media as any)}
      isUploading={isPending}
      sessionKey={sessionKey}
      uploadedPhotos={transformMedia(
        singleFamilyId ? unitMedia || [] : fetchedPropertyMedia || []
      )}
      onDeleteSuccess={onSuccess}
      onMakeDefaultSuccess={onSuccess}
      editDeniedTooltip={
        singleFamilyId ? unitEditDeniedTooltip : propertyEditDeniedTooltip
      }
    />
  );
};
